/* eslint-disable */
/*!
   _____ ______ __  __ _    _  _____ __  __ ______ _____  
  / ____|  ____|  \/  | |  | |/ ____|  \/  |  ____|  __ \ 
 | |  __| |__  | \  / | |__| | (___ | \  / | |__  | |  | |
 | | |_ |  __| | |\/| |  __  |\___ \| |\/| |  __| | |  | |
 | |__| | |____| |  | | |  | |____) | |  | | |____| |__| |
  \_____|______|_|  |_|_|  |_|_____/|_|  |_|______|_____/                                                                                                                                                                                                                                                                                                   
=========================================================
* GEMHSMED UI - v2.0.0
=========================================================

* Copyright 2024 GEMHS (https://gemhs.net/)

* Designed and Coded by GEMHS

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software. 

*/

import React, { useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useGetPatientVisitQuery, useGetUsersQuery } from "store";
import {
    Button,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Text,
    Box,
    Link as ChakraLink,
    Grid,
    GridItem,
    Table,
    Tbody,
    Tr,
    Th,
    Td,
    Spinner,
    Center,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet-async";
import moment from "moment";

const VisitDetails = () => {
    const { patientId, visitId } = useParams();
    const { data: visit, error, isLoading } = useGetPatientVisitQuery({
        patientId,
        visitId,
    });
    const navigate = useNavigate();

    const handleUpdateClick = (patientId, visitId) => {
        if (patientId && visitId) {
            navigate(`/patients/${patientId}/visits/${visitId}/update`);
        } else {
            console.error("Invalid patientId or visitId");
        }
    };

    const Day = moment(visit?.date, "YYYY,MM,DD").toDate();

    const formattedDay = Day.toLocaleDateString("en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });

    const time = visit?.time;
    const formattedTime = moment(time, "HH:mm:ss.SSSSSS").format("h:mm A");

    // Fetch users data
    const { data: users, isLoading: isLoadingUsers } = useGetUsersQuery();

    const userMap = useMemo(() => {
        const map = {};
        if (users && users.results) {
            users.results.forEach((user) => {
                if (user.id && user.username) {
                    map[user.id] = user.username;
                }
            });
        }
        return map;
    }, [users]);

    const calculateAge = (dateOfBirth) => {
        if (!dateOfBirth) return "Unknown"; // Handle cases where the date_of_birth is missing
        const birthDate = moment(dateOfBirth);
        const today = moment();
        return today.diff(birthDate, "years"); // Calculate the difference in years
    };

    if (isLoading) {
        return (
            <Center>
                <Spinner size="xl" />
            </Center>
        );
    }

    if (error) {
        return <Text> Error fetching user details: {error.message}</Text>;
    }

    return (
        <>
            <Helmet>
                <title>Visit Details</title>
            </Helmet>
            <Box mt={20} p={4}>
                <Card
                    boxShadow="md"
                    borderRadius="2xl"
                    overflow="hidden"
                    p={4}
                    border="1px solid"
                    borderColor="gray.300"
                >
                    <CardHeader borderBottom="1px solid" borderColor="blue.200">
                        <Box textAlign="center" mb={4}>
                            <Text fontSize="2xl" fontWeight="bold">
                                {visit.patient_details.name} {visit.patient_details.surname} Details
                            </Text>
                        </Box>
                    </CardHeader>

                    <CardBody>
                        <Box
                            overflow="auto"
                            border="1px solid"
                            borderRadius="md"
                            borderColor="gray.300"
                        >
                            <Table
                                variant="striped"
                                colorScheme="blue"
                                size="md"
                                borderRadius="md"
                                overflow="hidden"
                            >
                                <Tbody>
                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Patient Name</strong>
                                        </Td>
                                        <Td fontSize="md">
                                            {visit.patient_details?.name}
                                        </Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Patient Surname</strong>
                                        </Td>
                                        <Td fontSize="md">
                                            {visit.patient_details.surname}
                                        </Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Patient Age</strong>
                                        </Td>
                                        <Td fontSize="md">
                                            {visit.patient_details.date_of_birth
                                                ? calculateAge(
                                                      visit.patient_details
                                                          .date_of_birth
                                                  )
                                                : "N/A"}
                                        </Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Patient Address</strong>
                                        </Td>
                                        <Td fontSize="md">{visit.patient_details.address}</Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Date Of Visit</strong>
                                        </Td>
                                        <Td fontSize="md">{formattedDay}</Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Time Of Visit</strong>
                                        </Td>
                                        <Td fontSize="md">{formattedTime}</Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Reason For Visit</strong>
                                        </Td>
                                        <Td fontSize="md">{visit.reason}</Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Clinical Notes</strong>
                                        </Td>
                                        <Td fontSize="md">
                                            {visit.visit_notes}
                                        </Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Diagnosis</strong>
                                        </Td>
                                        <Td fontSize="md">{visit.diagnosis}</Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Treatment</strong>
                                        </Td>
                                        <Td fontSize="md">{visit.treatment}</Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>HEENT</strong>
                                        </Td>
                                        <Td fontSize="md">{visit.heent}</Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>CNS</strong>
                                        </Td>
                                        <Td fontSize="md">{visit.cns}</Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Chest</strong>
                                        </Td>
                                        <Td fontSize="md">{visit.chest}</Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>CVS</strong>
                                        </Td>
                                        <Td fontSize="md">{visit.cvs}</Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Genitalia</strong>
                                        </Td>
                                        <Td fontSize="md">{visit.genitalia}</Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Pulse</strong>
                                        </Td>
                                        <Td fontSize="md">{visit.pulse}</Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>BP</strong>
                                        </Td>
                                        <Td fontSize="md">{visit.bp}</Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>SPO2</strong>
                                        </Td>
                                        <Td fontSize="md">{visit.spo2}</Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Ext</strong>
                                        </Td>
                                        <Td fontSize="md">{visit.ext}</Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Abdomen</strong>
                                        </Td>
                                        <Td fontSize="md">{visit.abdomen}</Td>
                                    </Tr>

                                    <Tr>
                                        <Td fontSize="md">
                                            <strong>Weight</strong>
                                        </Td>
                                        <Td fontSize="md">{visit.weight}</Td>
                                    </Tr>

                                    {isLoadingUsers ? (
                                        <Tr>
                                            <Td fontSize="md">
                                                <strong>Registered By</strong>
                                            </Td>
                                            <Td fontSize="md">
                                                <Spinner size="sm" />{" "}
                                            </Td>
                                        </Tr>
                                    ) : (
                                        <Tr>
                                            <Td fontSize="md">
                                                <strong>Registered By</strong>
                                            </Td>
                                            <Td fontSize="md">
                                                {userMap[visit.registered_by] ||
                                                    visit.registered_by}
                                            </Td>
                                        </Tr>
                                    )}
                                </Tbody>
                            </Table>
                        </Box>
                    </CardBody>

                    <CardFooter
                        p={4}
                        borderTop="1px solid"
                        borderColor="gray.300"
                    >
                        <Grid
                            templateColumns="repeat(2, 1fr)"
                            gap={4}
                            width="100%"
                        >
                            <GridItem>
                                <ChakraLink
                                    as={Link}
                                    to={`/patients/${patientId}/visits`}
                                    color="blue.500"
                                >
                                    Back to List
                                </ChakraLink>
                            </GridItem>
                            <GridItem textAlign="right">
                                <Button
                                    colorScheme="blue"
                                    onClick={(e) => {
                                        handleUpdateClick(patientId, visitId);
                                        e.stopPropagation();
                                    }}
                                >
                                    Update
                                </Button>
                            </GridItem>
                        </Grid>
                    </CardFooter>
                </Card>
            </Box>
        </>
    );
};

export default VisitDetails;
